import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { ShopOrderStatus } from '@popcart/core/v1';
import { api } from '../api';
import { ExpressChip, Loading, useTitle } from '../ui/bits';
import { Fig } from '../ui/viz';
import { Scene, SceneError } from '../catalog/art';
import { clock, count, paymentLabel } from '../lib/format.js';
import { STATUS_VIEW, itemSummary, sortQueue } from '../lib/queue.js';
import { shopKeys, useShop } from './ShopLayout';
import { LineDots, StatusSticker, waitText } from './StatusSticker';
import { Select } from '../ui/controls';

type Filter = ShopOrderStatus | 'open';
const FILTERS: { value: Filter; label: string }[] = [
  { value: 'open', label: 'Open orders' },
  { value: 'placed', label: 'New' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'preparing', label: 'Preparing' },
  { value: 'ready', label: 'Ready' },
  { value: 'picked_up', label: 'Picked up' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];
const rise = (i: number) => ({ ['--i' as string]: i }) as CSSProperties;

export function OrdersPage() {
  useTitle('Orders');
  const { shopId, base, queue, queueQuery } = useShop();
  const [status, setStatus] = useState<Filter>('open');
  const filtered = useQuery({
    queryKey: shopKeys.orders(shopId, status),
    queryFn: () => api.shopQueue(shopId, status === 'open' ? undefined : status),
    enabled: status !== 'open',
    refetchInterval: 15_000,
  });
  const q = status === 'open' ? queueQuery : filtered;
  const rows = status === 'open' ? queue : sortQueue(filtered.data ?? []);

  // The live queue at a glance; each tile is a quick filter.
  const n = (s: ShopOrderStatus) => queue.filter((o) => o.status === s).length;
  const inHand = n('accepted') + n('preparing');
  const lateN = queue.filter((o) => o.late && o.status !== 'picked_up' && o.status !== 'delivered' && o.status !== 'cancelled').length;
  const tiles: { key: string; label: string; value: number; dot: string; filter: Filter; bad?: boolean }[] = [
    { key: 'new', label: 'New', value: n('placed'), dot: 'new', filter: 'placed' },
    { key: 'hand', label: 'In hand', value: inHand, dot: '', filter: 'preparing' },
    { key: 'ready', label: 'Ready', value: n('ready'), dot: 'good', filter: 'ready' },
    { key: 'late', label: 'Late', value: lateN, dot: 'bad', filter: 'open', bad: lateN > 0 },
  ];

  return (
    <>
      <header className="dv-head">
        <div className="stack s4">
          <h1>Orders</h1>
          <span className="dv-live">{rows.length} shown</span>
        </div>
        <div className="dv-actions">
          <div className="field" style={{ minWidth: 200 }}>
            <label htmlFor="order-filter" className="sr-only">
              Show
            </label>
            <Select id="order-filter" className="input" value={status} onChange={(e) => setStatus(e.target.value as Filter)}>
              {FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </header>

      <div className="ph-statusbar dv-rise" style={rise(0)} role="group" aria-label="Quick filters">
        {tiles.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`ph-sbtn${t.bad ? ' bad' : ''}`}
            aria-pressed={t.key !== 'late' && status === t.filter}
            aria-label={`${t.label}: ${t.value}`}
            onClick={() => setStatus(t.filter)}
          >
            <span className="dv-label">
              <i className={t.dot} aria-hidden="true" />
              {t.label}
            </span>
            <Fig text={count(t.value)} size="lg" />
          </button>
        ))}
      </div>

      <section className="card dv-card dv-rise" style={rise(1)} aria-label="Orders">
        {q.isError ? (
          <SceneError error={q.error} onRetry={() => void q.refetch()} />
        ) : q.isLoading ? (
          <Loading label="Loading orders" rows={3} />
        ) : rows.length ? (
          <div className="table-wrap">
            <table className="ph-table">
              <thead>
                <tr>
                  <th scope="col">Order</th>
                  <th scope="col">Placed</th>
                  <th scope="col">Items</th>
                  <th scope="col">Customer</th>
                  <th scope="col">Payment</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="num">
                    Waiting
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.shopOrderId}>
                    <td>
                      <div className="row">
                        <Link to={`${base}?order=${encodeURIComponent(o.shopOrderId)}`} className="ph-code">
                          {o.code}
                        </Link>
                        <LineDots lines={o.lines} />
                        {o.speed === 'express' ? <ExpressChip /> : null}
                      </div>
                    </td>
                    <td className="num" style={{ textAlign: 'left' }}>
                      {clock(o.placedAt)}
                    </td>
                    <td>{itemSummary(o, 2)}</td>
                    <td>
                      {o.customerFirstName}
                      <span className="sub">{o.addressShort}</span>
                    </td>
                    <td>{paymentLabel(o.payment)}</td>
                    <td>
                      <StatusSticker tone={STATUS_VIEW[o.status].tone}>{STATUS_VIEW[o.status].label}</StatusSticker>
                    </td>
                    <td className="num">
                      <span className={`ph-wait${o.late ? ' late' : ''}`} title={`${o.minutesWaiting} min`}>
                        {o.late ? 'Late · ' : ''}
                        {waitText(o.minutesWaiting)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Scene scene="noOrders" title={status === 'open' ? 'All quiet for now' : 'No orders here'}>
            {status === 'open' ? 'New orders pop up on Today with a sound.' : 'Nothing with that status. Try another filter.'}
          </Scene>
        )}
      </section>
    </>
  );
}
